import React, { useCallback, useRef, useState } from 'react'

import { faArrowToBottom, faTrash, faUserCheck } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, IconButton, MenuItem, TextField, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { GridApi, GridToolbarContainer } from '@mui/x-data-grid-pro'
import { NonDismissibleDialog } from '@scoir/modals'
import { Tooltip } from '@scoir/tooltips'

import api from 'common/api'
import useSnackbar from 'common/use-snackbar'
import { useAppSelector } from 'store'
import { selectScid } from 'store/college'

import SearchTextField from '../components/TableComponents/SearchTextField'
import {
  ApiRecord,
  BulkActionsBody,
  BulkReassignBody,
  BulkReassignResponse,
  BulkUnregisterResponse,
  RepResponse,
} from './types'

const { Body, Footer } = NonDismissibleDialog
const { PrimaryActionButton, CloseButton } = Footer

const CustomToolbar = ({
  filterByHighSchoolSearch,
  apiRef,
  selectedRows,
  setRefreshAfterBulkAction,
}: {
  filterByHighSchoolSearch: (value: string) => void
  apiRef: React.MutableRefObject<GridApi>
  selectedRows: ApiRecord[]
  setRefreshAfterBulkAction: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const scid = useAppSelector(selectScid)
  const hasFetchedReps = useRef(false)
  const [reps, setReps] = useState<RepResponse[]>([])

  const fetchRepsOnce = useCallback(() => {
    // Fetch reps only once when the reassign action is triggered for the first time
    if (!hasFetchedReps.current) {
      hasFetchedReps.current = true
      api.get<RepResponse[]>(`/api/colleges/${scid}/reps`).then(setReps)
    }
  }, [scid])

  return (
    <GridToolbarContainer>
      <Box
        display="flex"
        flexWrap="wrap"
        gridGap={24}
        alignItems="center"
        justifyContent="space-between"
        padding={2}
        width="100%"
      >
        <Box
          display="flex"
          gridGap={16}
          alignItems="center"
        >
          <Box minWidth={400}>
            <SearchTextField
              defaultValue={''}
              onChange={(val) => filterByHighSchoolSearch(val ?? '')}
            />
          </Box>
          {selectedRows.length > 0 && (
            <>
              <Typography>{`${selectedRows.length} Selected`}</Typography>
              <ResponsiveBulkButtons
                selectedRows={selectedRows}
                reps={reps}
                onReassignOpen={fetchRepsOnce}
                setRefreshAfterBulkAction={setRefreshAfterBulkAction}
              />
            </>
          )}
        </Box>
        <ResponsiveExportButton apiRef={apiRef} />
      </Box>
    </GridToolbarContainer>
  )
}

const useStyles = makeStyles((theme) => ({
  startIcon: {
    height: 18,
    width: 18,
  },
  select: {
    marginTop: theme.spacing(2),
  },
}))
const ResponsiveBulkButtons = ({
  selectedRows,
  reps,
  onReassignOpen,
  setRefreshAfterBulkAction,
}: {
  selectedRows: ApiRecord[]
  reps: RepResponse[]
  onReassignOpen: () => void
  setRefreshAfterBulkAction: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('md'))
  const [bulkAction, setBulkAction] = useState<'reassign' | 'unregister' | null>(null)
  const [selectedRepUserId, setSelectedRepUserId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, hasError] = useState(false)

  const setReassignSnackbarError = useSnackbar(
    'Some visits could not be reassigned. Please try again or contact support if the issue persists.',
  )
  const setUnregisterSnackbarError = useSnackbar(
    'Some visits could not be unregistered. Please try again or contact support if the issue persists.',
  )

  const handleRepSelection = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedRepUserId(event.target.value as string)
  }

  const unregisterForVisits = () => {
    api
      .post<BulkActionsBody[], BulkUnregisterResponse>(
        '/api/highschools/visits/bulkUnregister',
        selectedRows.map(({ id, rep }) => ({ EventId: id, RepId: rep.repId })),
      )
      .then((response) => {
        if (response.UnregisteredEventIds.length === 0) {
          hasError(true)
          setLoading(false)
          return
        } else if (response.UnregisteredEventIds.length < selectedRows.length) {
          // If some visits were not unregistered, show an error but still refresh the successfully unregistered visits
          setUnregisterSnackbarError()
        }
        afterBulkAction()
      })
      .catch(() => {
        hasError(true)
        setLoading(false)
      })
  }

  const reassignVisits = () => {
    if (!selectedRepUserId) return
    // Filter out any visits that are already assigned to the selected representative to avoid unnecessary API calls
    const filteredSelectedRows = selectedRows.filter(({ rep }) => rep.userId !== selectedRepUserId)
    if (filteredSelectedRows.length === 0) {
      onClose()
      return
    }
    api
      .post<BulkReassignBody, BulkReassignResponse>('/api/highschools/visits/bulkReassign', {
        Visits: filteredSelectedRows.map(({ id, rep }) => ({ EventId: id, RepId: rep.repId })),
        NewRepId: selectedRepUserId,
      })
      .then((response) => {
        if (response.ReassignedEventIds.length === 0) {
          hasError(true)
          setLoading(false)
          return
        } else if (response.ReassignedEventIds.length < filteredSelectedRows.length) {
          // If some visits were not reassigned, show an error but still refresh the successfully reassigned visits
          setReassignSnackbarError()
        }
        afterBulkAction()
      })
      .catch(() => {
        hasError(true)
        setLoading(false)
      })
  }

  const afterBulkAction = () => {
    setRefreshAfterBulkAction(true)
    onClose()
  }

  const onClose = () => {
    setSelectedRepUserId(null)
    setBulkAction(null)
    hasError(false)
    setLoading(false)
  }

  return (
    <>
      {isMediumScreenSize ? (
        <>
          <Tooltip title="Reassign Users">
            <IconButton
              color="primary"
              onClick={() => {
                onReassignOpen()
                setBulkAction('reassign')
              }}
            >
              <FontAwesomeIcon icon={faUserCheck} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel Visits">
            <IconButton
              color="primary"
              onClick={() => setBulkAction('unregister')}
            >
              <FontAwesomeIcon icon={faTrash} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onReassignOpen()
              setBulkAction('reassign')
            }}
            startIcon={
              <FontAwesomeIcon
                icon={faUserCheck}
                className={classes.startIcon}
              />
            }
          >
            Reassign Users
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setBulkAction('unregister')}
            startIcon={
              <FontAwesomeIcon
                icon={faTrash}
                className={classes.startIcon}
              />
            }
          >
            Unregister Visits
          </Button>
        </>
      )}
      <NonDismissibleDialog
        open={bulkAction !== null}
        headerText={bulkAction === 'reassign' ? 'Reassign Users' : 'Unregister Visits'}
        onClose={onClose}
      >
        <Body>
          <Typography>
            {bulkAction === 'reassign'
              ? `You are about to reassign ${selectedRows.length} ${
                  selectedRows.length === 1 ? 'visit' : 'visits'
                } to a new Admissions Representative. The new representative will receive an email notification.`
              : `Are you sure you want to unregister for the ${selectedRows.length} selected ${
                  selectedRows.length === 1 ? 'visit' : 'visits'
                }?`}
          </Typography>
          {bulkAction === 'reassign' && (
            <TextField
              className={classes.select}
              select
              disabled={reps.length === 0}
              variant="outlined"
              label="Select Admissions Representative"
              fullWidth
              helperText={reps.length === 0 ? 'No available representatives to reassign to.' : ''}
              onChange={handleRepSelection}
            >
              {reps.map((rep) => (
                <MenuItem
                  key={rep.Id}
                  value={rep.Id}
                >
                  {`${rep.FirstName} ${rep.LastName}`.trim()}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Body>
        <Footer>
          <Typography color="error">
            {error ? 'An error occurred while processing your request. Please try again.' : ''}
          </Typography>
          <CloseButton>Cancel</CloseButton>
          <PrimaryActionButton
            disabled={loading || (bulkAction === 'reassign' && (reps.length === 0 || !selectedRepUserId))}
            onClick={() => {
              setLoading(true)
              if (bulkAction === 'reassign') {
                reassignVisits()
              } else {
                unregisterForVisits()
              }
            }}
          >
            {bulkAction === 'reassign' ? 'Reassign' : 'Unregister'}
          </PrimaryActionButton>
        </Footer>
      </NonDismissibleDialog>
    </>
  )
}

const ResponsiveExportButton = ({ apiRef }: { apiRef: React.MutableRefObject<GridApi> }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('md'))

  const handleExport = () =>
    apiRef.current.exportDataAsCsv({
      fileName: `visits-export-${new Date().toLocaleDateString().replace(/\//g, '-')}`,
      delimiter: ',',
    })
  return isMediumScreenSize ? (
    <Tooltip title="Export">
      <IconButton
        color="primary"
        onClick={handleExport}
      >
        <FontAwesomeIcon icon={faArrowToBottom} />
      </IconButton>
    </Tooltip>
  ) : (
    <Button
      color="primary"
      onClick={handleExport}
      startIcon={
        <FontAwesomeIcon
          icon={faArrowToBottom}
          className={classes.startIcon}
        />
      }
    >
      Export
    </Button>
  )
}

export default CustomToolbar
